// frontend/src/site/pages/SiteImoveis.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { useSite } from '../SiteContext';
import useSiteCatalog from '../hooks/useSiteCatalog';

import SiteFilterBar from '../components/SiteFilterBar';
import SiteListingGrid from '../components/SiteListingGrid';

import { FaInstagram, FaFacebookF, FaWhatsapp } from 'react-icons/fa';

function asStr(v) {
  return String(v ?? '').trim();
}
function toNumberOrEmpty(v) {
  const s = asStr(v);
  if (!s) return '';
  const n = Number(s.replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? String(n) : '';
}

export default function SiteImoveis() {
  const { handle: handleParam } = useParams();
  const handle = asStr(handleParam);

  // ✅ Fonte única (SiteProvider resolve handle => corretorId/perfilSite)
  const {
    corretorId,
    perfilSite,
    ownerWhatsUrl,
    loading: siteLoading,
  } = useSite();

  const [sp, setSp] = useSearchParams();

  // ---------- HERO (capa + chips) ----------
  const cover = useMemo(() => {
    return (
      perfilSite?.siteCoverUrl ||
      perfilSite?.perfil?.coverUrl ||
      perfilSite?.coverUrl ||
      'data:image/svg+xml;utf8,' +
        encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="900">
             <defs><linearGradient id="g" x1="0" x2="1">
               <stop stop-color="#eff6ff"/><stop offset="1" stop-color="#e5e7eb"/>
             </linearGradient></defs>
             <rect width="100%" height="100%" fill="url(#g)"/>
           </svg>`
        )
    );
  }, [perfilSite]);

  const ig = perfilSite?.instagramUrl || perfilSite?.perfil?.instagramUrl || '';
  const fb = perfilSite?.facebookUrl || perfilSite?.perfil?.facebookUrl || '';
  const whatsChip =
    perfilSite?.whatsappDisplay || perfilSite?.perfil?.whatsappDisplay || '';

  // ---------- FILTROS (via querystring) ----------
  const filtros = useMemo(() => {
    const tipo = asStr(sp.get('tipo')); // slug ou title
    const ref = asStr(sp.get('ref'));
    const dorm = asStr(sp.get('dorm'));
    const min = toNumberOrEmpty(sp.get('min'));
    const max = toNumberOrEmpty(sp.get('max'));

    return {
      publicado: true,
      ...(tipo ? { tipo } : {}),
      ...(ref ? { ref } : {}),
      ...(dorm ? { dorm } : {}),
      ...(min ? { min } : {}),
      ...(max ? { max } : {}),
    };
  }, [sp]);

  const filtrosKey = useMemo(() => JSON.stringify(filtros), [filtros]);

  // ---------- LISTAGEM PAGINADA (catálogo público) ----------
  const {
    docs: items,
    load,
    loading,
    exhausted,
    onLoadMore,
  } = useSiteCatalog(corretorId || '', filtros, 24);

  // primeira carga e sempre que filtros/corretorId mudarem
  useEffect(() => {
    if (!corretorId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corretorId, filtrosKey]);

  // ---------- UI controls (inputs locais p/ querystring) ----------
  const [form, setForm] = useState({
    tipo: asStr(sp.get('tipo')),
    ref: asStr(sp.get('ref')),
    dorm: asStr(sp.get('dorm')),
    min: asStr(sp.get('min')),
    max: asStr(sp.get('max')),
  });

  useEffect(() => {
    setForm({
      tipo: asStr(sp.get('tipo')),
      ref: asStr(sp.get('ref')),
      dorm: asStr(sp.get('dorm')),
      min: asStr(sp.get('min')),
      max: asStr(sp.get('max')),
    });
  }, [sp]);

  function applyFilters(e) {
    e?.preventDefault?.();

    const next = {};
    if (asStr(form.tipo)) next.tipo = asStr(form.tipo);
    if (asStr(form.ref)) next.ref = asStr(form.ref);
    if (asStr(form.dorm)) next.dorm = asStr(form.dorm);
    if (toNumberOrEmpty(form.min)) next.min = toNumberOrEmpty(form.min);
    if (toNumberOrEmpty(form.max)) next.max = toNumberOrEmpty(form.max);

    setSp(next, { replace: false });
  }

  function clearFilters() {
    setForm({ tipo: '', ref: '', dorm: '', min: '', max: '' });
    setSp({}, { replace: false });
  }

  // ✅ estado correto:
  // - enquanto SiteProvider está resolvendo => loading
  // - quando terminou e não achou corretorId => 404 do site
  const isMissingSite = !siteLoading && !corretorId;

  return (
    <div className="min-h-screen bg-[color:var(--bg)] text-[color:var(--text)]">
      {/* HERO */}
      <header className="relative">
        <div className="relative w-full min-h-[100svh] md:min-h-screen">
          <img
            src={cover}
            alt="Capa do site"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/5 to-black/25" />

          {/* Chips no topo-direito */}
          <div className="absolute right-6 top-6 z-10 flex items-center gap-3">
            {!!whatsChip && (
              <a
                href={ownerWhatsUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1 rounded-full bg-[color:var(--card)] border border-[color:var(--border)] text-sm shadow flex items-center gap-2 text-[color:var(--text)] hover:opacity-90"
                style={{ borderRadius: 'var(--radius)' }}
              >
                <FaWhatsapp className="text-green-600" />
                {whatsChip}
              </a>
            )}

            {!!fb && (
              <a
                href={fb}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-[color:var(--card)] border border-[color:var(--border)] shadow text-[color:var(--text)] hover:opacity-90"
                aria-label="Facebook"
                style={{ borderRadius: '9999px' }}
              >
                <FaFacebookF />
              </a>
            )}

            {!!ig && (
              <a
                href={ig}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-[color:var(--card)] border border-[color:var(--border)] shadow text-[color:var(--text)] hover:opacity-90"
                aria-label="Instagram"
                style={{ borderRadius: '9999px' }}
              >
                <FaInstagram />
              </a>
            )}
          </div>

          {/* FilterBar na base (legado) */}
          <div className="absolute inset-x-0 bottom-6 z-10">
            <div className="max-w-7xl mx-auto px-6">
              <SiteFilterBar />
            </div>
          </div>
        </div>
      </header>

      {/* FILTROS (querystring) */}
      <div className="max-w-7xl mx-auto px-6 pt-8">
        <form
          onSubmit={applyFilters}
          className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 md:p-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-3">
              <label className="text-xs text-[color:var(--muted)]">Tipo</label>
              <input
                value={form.tipo}
                onChange={(e) =>
                  setForm((p) => ({ ...p, tipo: e.target.value }))
                }
                placeholder="Ex: casa, apartamento..."
                className="mt-1 w-full px-3 py-2 rounded-xl border border-[color:var(--border)] bg-transparent"
              />
              <p className="text-[11px] text-[color:var(--muted)] mt-1">
                Pode ser “casa” ou “Casa”.
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="text-xs text-[color:var(--muted)]">Ref</label>
              <input
                value={form.ref}
                onChange={(e) =>
                  setForm((p) => ({ ...p, ref: e.target.value }))
                }
                placeholder="Ex: TC001"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-[color:var(--border)] bg-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs text-[color:var(--muted)]">
                Dormitórios (mín.)
              </label>
              <input
                value={form.dorm}
                onChange={(e) =>
                  setForm((p) => ({ ...p, dorm: e.target.value }))
                }
                placeholder="Ex: 2"
                inputMode="numeric"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-[color:var(--border)] bg-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs text-[color:var(--muted)]">
                Preço mín.
              </label>
              <input
                value={form.min}
                onChange={(e) =>
                  setForm((p) => ({ ...p, min: e.target.value }))
                }
                placeholder="Ex: 250000"
                inputMode="numeric"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-[color:var(--border)] bg-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs text-[color:var(--muted)]">
                Preço máx.
              </label>
              <input
                value={form.max}
                onChange={(e) =>
                  setForm((p) => ({ ...p, max: e.target.value }))
                }
                placeholder="Ex: 800000"
                inputMode="numeric"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-[color:var(--border)] bg-transparent"
              />
            </div>

            <div className="md:col-span-1 flex items-end gap-2">
              <button
                type="submit"
                className="w-full px-4 py-2 rounded-xl bg-black text-white hover:opacity-90"
              >
                Filtrar
              </button>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-[color:var(--muted)] hover:opacity-80"
            >
              Limpar filtros
            </button>

            <div className="text-sm text-[color:var(--muted)]">
              {loading ? 'Carregando...' : `${items.length} imóveis`}
            </div>
          </div>
        </form>

        <div className="mt-4 flex justify-end">
          <Link
            to={`/u/${handle}`}
            className="px-4 py-2 rounded-xl border border-[color:var(--border)] hover:opacity-90"
          >
            Voltar ao site
          </Link>
        </div>
      </div>

      {/* LISTA */}
      <section
        className="max-w-7xl mx-auto px-6 pt-10 pb-16"
        aria-live="polite"
      >
        {siteLoading ? (
          <div className="text-center text-[color:var(--muted)] py-16">
            Carregando…
          </div>
        ) : isMissingSite ? (
          <div className="mt-8 rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-6">
            <div className="font-semibold">Site não encontrado</div>
            <div className="text-sm text-[color:var(--muted)] mt-1">
              Verifique o link /u/{handle} ou volte para a página anterior.
            </div>
          </div>
        ) : !items.length && loading ? (
          <div className="text-center text-[color:var(--muted)] py-16">
            Carregando…
          </div>
        ) : !items.length ? (
          <div className="mt-8 rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-6">
            <div className="font-semibold">Nenhum imóvel encontrado</div>
            <div className="text-sm text-[color:var(--muted)] mt-1">
              Ajuste os filtros ou volte para a página inicial.
            </div>
            <div className="mt-4">
              <Link
                to={`/u/${handle}`}
                className="inline-flex px-4 py-2 rounded-xl border border-[color:var(--border)] hover:opacity-90"
              >
                Voltar ao site
              </Link>
            </div>
          </div>
        ) : (
          <SiteListingGrid
            items={items}
            loading={loading}
            exhausted={exhausted}
            onLoadMore={onLoadMore}
            whatsappUrl={ownerWhatsUrl || ''}
            cols={{ base: 1, sm: 2, lg: 3, xl: 4 }}
          />
        )}
      </section>
    </div>
  );
}
